import { collection } from '@/infra/collection'
import {
  PersonalProfileVersion,
  PersonalProfileVersionId,
} from '@/features/tax-profile/individual-profiles/PersonalProfile'
import {
  SpousalProfileVersion,
  SpousalProfileVersionId,
} from '@/features/tax-profile/individual-profiles/SpousalProfile'
import {
  DependentProfileVersion,
  DependentProfileVersionId,
} from '@/features/tax-profile/individual-profiles/DependentProfile'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { TaxProfileApi } from '@/features/tax-profile/api/tax-profile-api'
import {
  GetPersonalProfileForJobRequest,
  GetPersonalProfileRequest,
  GetSpousalProfileForJobRequest,
  GetSpousalProfileRequest,
  SavePersonalProfileRequest,
  SaveSpousalProfileRequest,
  GetDependentProfileForJobRequest,
  GetDependentProfileRequest,
  CreateDependentProfileRequest,
  UpdateDependentProfileRequest,
  ListDependentProfilesRequest,
  DelistDependentProfileRequest,
} from '@taxfyle/api-internal/internal/individual_profile_pb'
import { Id } from '@/infra/id'
import {
  mapPersonalInfoToProto,
  mapPersonalProfileVersionProtoToProps,
  mapSpousalProfileVersionProtoToProps,
  mapDependentProfileVersionProtoToProps,
} from '@/features/tax-profile/individual-profiles/individual-profile-mappers'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import type { PersonalInfo } from './PersonalInfo'
import type { ContactInfo } from '../ContactInfo'
import { mapContactInfoToProto } from '../contact-info-mapper'
import { debounceTime, groupBy, mergeMap, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'

/**
 * Store for managing individual profiles.
 */
export class IndividualProfileStore {
  /**
   * Subject to emit on which triggers a refresh of the latest personal profile version.
   */
  private readonly _refreshLatestPersonalProfileVersion = new Subject<unknown>()

  /**
   * Subject to emit on which triggers a refresh of the latest spousal profile version.
   */
  private readonly _refreshLatestSpousalProfileVersion = new Subject<unknown>()

  /**
   * Subject to emit on which triggers a refresh of the latest dependent profile version.
   */
  private readonly _refreshLatestDependentProfileVersion = new Subject<Id>()

  /**
   * Emits when a dependent has been delisted.
   */
  private readonly _dependentDelisted = new Subject<Id>()

  /**
   * Observable that emits when a dependent has been delisted.
   */
  readonly dependentDelisted$ = this._dependentDelisted.asObservable()

  /**
   * Collection of personal profile versions.
   */
  readonly personalProfileVersions = collection({
    create: () => new PersonalProfileVersion(),
    getDataId: (d) => PersonalProfileVersionId.from(d.id, d.version),
    getModelId: (m) => PersonalProfileVersionId.from(m.id, m.version),
  })

  /**
   * Collection of spousal profile versions.
   */
  readonly spousalProfileVersions = collection({
    create: () => new SpousalProfileVersion(),
    getDataId: (d) => SpousalProfileVersionId.from(d.id, d.version),
    getModelId: (m) => SpousalProfileVersionId.from(m.id, m.version),
  })

  /**
   * Collection of dependent profile versions.
   */
  readonly dependentProfileVersions = collection({
    create: () => new DependentProfileVersion(),
    getDataId: (d) => DependentProfileVersionId.from(d.id, d.version),
    getModelId: (m) => DependentProfileVersionId.from(m.id, m.version),
  })

  constructor(
    private readonly _api: TaxProfileApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this._refreshLatestPersonalProfileVersion
      .pipe(
        switchMap(() =>
          withAbortSignal(this.fetchLatestPersonalProfileVersion.bind(this)),
        ),
      )
      .subscribe()

    this._refreshLatestSpousalProfileVersion
      .pipe(
        switchMap(() =>
          withAbortSignal(this.fetchLatestSpousalProfileVersion.bind(this)),
        ),
      )
      .subscribe()

    this._refreshLatestDependentProfileVersion
      .pipe(
        groupBy((profileId) => profileId, {
          duration: (source$) => source$.pipe(debounceTime(5_000)),
        }),
        mergeMap((profileId$) =>
          profileId$.pipe(
            switchMap((profileId) =>
              withAbortSignal((signal) =>
                this.fetchLatestDependentProfileVersion(profileId, signal),
              ),
            ),
          ),
        ),
      )
      .subscribe()
  }

  /**
   * Fetches the latest profile for the current workspace member.
   */
  async fetchLatestPersonalProfileVersion(
    signal?: AbortSignal,
  ): Promise<PersonalProfileVersion | null> {
    signal?.throwIfAborted()

    const request = new GetPersonalProfileRequest()
      // Profiles are workspace-scoped
      .setWorkspaceId(Id.assert(this._getCurrentMember().workspaceId))

    const response = await this._api.individualProfiles
      .getPersonalProfile(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapPersonalProfileVersionProtoToProps(response)
    return this.personalProfileVersions.set(props)
  }

  /**
   * Fetches a specific personal profile version by ID and version number.
   */
  async fetchPersonalProfileVersionForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<PersonalProfileVersion | null> {
    signal?.throwIfAborted()
    jobId = Id.assert(jobId)

    const request = new GetPersonalProfileForJobRequest().setJobId(jobId)

    const response = await this._api.individualProfiles
      .getPersonalProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapPersonalProfileVersionProtoToProps(response)
    return this.personalProfileVersions.set(props)
  }

  /**
   * Saves a new personal profile version.
   */
  async savePersonalProfileVersion(
    input: SavePersonalProfileVersionOptions,
  ): Promise<PersonalProfileVersion> {
    const request = new SavePersonalProfileRequest()
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
      .setContactInfo(mapContactInfoToProto(input.contactInfo))
      .setOccupation(input.occupation)
      .setWorkspaceId(Id.assert(this._getCurrentMember().workspaceId))

    const response =
      await this._api.individualProfiles.savePersonalProfile(request)

    const id = response.getId()
    const version = response.getVersion()

    // The info is more or less the same except for some vanity formatting,
    // so we can just set it as-is.
    const profile = this.personalProfileVersions.set({
      id,
      version,
      personalInfo: input.personalInfo,
      contactInfo: input.contactInfo,
      occupation: input.occupation,
    })

    // Refresh in the background so we pick up on the latest version.
    this._refreshLatestPersonalProfileVersion.next(null)

    return profile
  }

  /**
   * Fetches the latest spousal profile for the current workspace member.
   */
  async fetchLatestSpousalProfileVersion(
    signal?: AbortSignal,
  ): Promise<SpousalProfileVersion | null> {
    signal?.throwIfAborted()

    const request = new GetSpousalProfileRequest()
      // Profiles are workspace-scoped
      .setWorkspaceId(Id.assert(this._getCurrentMember().workspaceId))

    const response = await this._api.individualProfiles
      .getSpousalProfile(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapSpousalProfileVersionProtoToProps(response)
    return this.spousalProfileVersions.set(props)
  }

  /**
   * Fetches a specific spousal profile version by ID and version number.
   */
  async fetchSpousalProfileVersionForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<SpousalProfileVersion | null> {
    signal?.throwIfAborted()
    jobId = Id.assert(jobId)

    const request = new GetSpousalProfileForJobRequest().setJobId(jobId)

    const response = await this._api.individualProfiles
      .getSpousalProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapSpousalProfileVersionProtoToProps(response)
    return this.spousalProfileVersions.set(props)
  }

  /**
   * Saves a new spousal profile version.
   */
  async saveSpousalProfileVersion(
    input: SaveSpousalProfileVersionOptions,
  ): Promise<SpousalProfileVersion> {
    const request = new SaveSpousalProfileRequest()
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
      .setOccupation(input.occupation)
      .setWorkspaceId(Id.assert(this._getCurrentMember().workspaceId))

    const response =
      await this._api.individualProfiles.saveSpousalProfile(request)

    const id = response.getId()
    const version = response.getVersion()

    // The info is more or less the same except for some vanity formatting,
    // so we can just set it as-is.
    const profile = this.spousalProfileVersions.set({
      id,
      version,
      personalInfo: input.personalInfo,
      occupation: input.occupation,
    })

    // Refresh in the background so we pick up on the latest version.
    this._refreshLatestSpousalProfileVersion.next(null)

    return profile
  }

  /**
   * Fetches the latest dependent profile for the current workspace member.
   */
  async fetchLatestDependentProfileVersion(
    profileId: Id,
    signal?: AbortSignal,
  ): Promise<DependentProfileVersion | null> {
    signal?.throwIfAborted()

    const request = new GetDependentProfileRequest().setId(Id.assert(profileId))

    const response = await this._api.individualProfiles
      .getDependentProfile(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapDependentProfileVersionProtoToProps(response)
    return this.dependentProfileVersions.set(props)
  }

  /**
   * Fetches a specific dependent profile version by ID and version number.
   */
  async fetchDependentProfileVersionForJob(
    jobId: Id,
    profileId: Id,
    signal?: AbortSignal,
  ): Promise<DependentProfileVersion | null> {
    signal?.throwIfAborted()
    jobId = Id.assert(jobId)

    const request = new GetDependentProfileForJobRequest()
      .setId(Id.assert(profileId))
      .setJobId(jobId)

    const response = await this._api.individualProfiles
      .getDependentProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)

    signal?.throwIfAborted()

    if (!response) {
      return null
    }

    const props = mapDependentProfileVersionProtoToProps(response)
    return this.dependentProfileVersions.set(props)
  }

  /**
   * Saves a new dependent profile version.
   */
  async saveDependentProfileVersion(
    input: SaveDependentProfileVersionOptions,
  ): Promise<DependentProfileVersion> {
    const isUpdate = !!input.id

    let id
    let version
    if (!isUpdate) {
      // Create a new profile
      const request = new CreateDependentProfileRequest()
        .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
        .setRelationship(input.relationship)
        .setWorkspaceId(Id.assert(this._getCurrentMember().workspaceId))

      const response =
        await this._api.individualProfiles.createDependentProfile(request)

      id = response.getId()
      version = response.getVersion()
    } else {
      // Update existing profile
      const request = new UpdateDependentProfileRequest()
        .setId(Id.assert(input.id))
        .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
        .setRelationship(input.relationship)

      const response =
        await this._api.individualProfiles.updateDependentProfile(request)

      id = response.getId()
      version = response.getVersion()
    }

    // The info is more or less the same except for some vanity formatting,
    // so we can just set it as-is.
    const profile = this.dependentProfileVersions.set({
      id,
      version,
      personalInfo: input.personalInfo,
      relationship: input.relationship,
    })

    // Refresh in the background so we pick up on the latest version.
    this._refreshLatestDependentProfileVersion.next(id)

    return profile
  }

  /**
   * Lists the dependents for the current workspace member.
   *
   * @param query
   * @param signal
   */
  async listDependents(
    { paging }: ListDependentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<DependentProfileVersion>> {
    signal?.throwIfAborted()
    const workspaceId = Id.assert(this._getCurrentMember().workspaceId)

    const response = await this._api.individualProfiles.listDependentProfiles(
      new ListDependentProfilesRequest()
        .setWorkspaceId(workspaceId)
        .setPageToken(paging?.after ?? '')
        .setPageSize(paging?.limit ?? 50),
    )

    signal?.throwIfAborted()
    const props = response
      .getDependentProfilesList()
      .map(mapDependentProfileVersionProtoToProps)
    const profiles = this.dependentProfileVersions.set(props)
    return CursorPaged.of(profiles, response.getNextPageToken() || null)
  }

  /**
   * Deletes the profile.
   *
   * @param id
   */
  async deleteDependentProfile(id: Id) {
    id = Id.assert(id)
    await this._api.individualProfiles.delistDependentProfile(
      new DelistDependentProfileRequest().setId(id),
    )
    this._dependentDelisted.next(id)
  }
}

/**
 * Options for saving a personal profile version.
 */
export interface SavePersonalProfileVersionOptions {
  /**
   * The personal information to save.
   */
  personalInfo: PersonalInfo

  /**
   * The contact information to save.
   */
  contactInfo: ContactInfo

  /**
   * The occupation to save.
   */
  occupation: string
}

/**
 * Options for saving a spousal profile version.
 */
export interface SaveSpousalProfileVersionOptions {
  /**
   * The personal information to save.
   */
  personalInfo: PersonalInfo

  /**
   * The occupation to save (optional for spousal profiles).
   */
  occupation: string
}

/**
 * Options for saving a dependent profile version.
 */
export interface SaveDependentProfileVersionOptions {
  /**
   * The profile ID (optional for new profiles).
   */
  id?: Id

  /**
   * The personal information to save.
   */
  personalInfo: PersonalInfo

  /**
   * The relationship to save.
   */
  relationship: string
}

/**
 * Query for listing dependents for a user.
 */
export interface ListDependentsQuery {
  paging?: CursorPageQuery
}
